import { useCallback, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { Toggle } from '@/lib/base/forms/toggle';
import { AlertFloating } from '@/lib/base/overlays';

import { useHubspotMappings } from '../hooks/useHubspotMappings';
import { ConnectionControls } from './ConnectionControls';
import { HubspotMappingsManagerView } from './HubspotMappingsManagerView';

interface HubspotSetupPanelProps {
  orgId: number | string;
  onFinished: () => void;
}

export function HubspotSetupPanel({
  orgId,
  onFinished,
}: HubspotSetupPanelProps) {
  const { completeSetup } = useHubspotMappings(orgId, { enabled: true });
  const [backfill, setBackfill] = useState(false);

  const handleBackfillChange = useCallback((isSelected: boolean) => {
    setBackfill(isSelected);
  }, []);

  const handleComplete = useCallback(async () => {
    try {
      await completeSetup.mutateAsync(backfill);
      onFinished();
    } catch {
      /* surfaced via completeSetup.isError below */
    }
  }, [completeSetup, backfill, onFinished]);

  return (
    <div className='flex flex-col gap-6'>
      <div>
        <h2 className='text-heading-3'>Finish setting up HubSpot</h2>
        <p className='text-body text-[var(--color-text-secondary)] mt-1'>
          Configure your field mappings and connection settings. Completing
          setup begins syncing.
        </p>
      </div>

      <HubspotMappingsManagerView orgId={orgId} connected />

      <ConnectionControls orgId={orgId} />

      <Toggle
        label='Push existing Ahoy records to HubSpot'
        hint='Backfill your existing Ahoy contacts, companies, and deals into HubSpot. Records also sync going forward.'
        isSelected={backfill}
        onChange={handleBackfillChange}
      />

      {completeSetup.isError && (
        <AlertFloating
          title="We couldn't complete setup"
          description='Something went wrong. Try again, and get in touch if it keeps happening.'
          color='error'
        />
      )}

      <p className='text-body text-[var(--color-text-secondary)]'>
        Completing setup starts ongoing two-way sync between Ahoy and HubSpot.
        Records keep syncing in both directions until you disconnect.
      </p>

      <Button
        color='primary'
        onClick={handleComplete}
        isLoading={completeSetup.isPending}
      >
        Complete setup
      </Button>
    </div>
  );
}
